import { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart,
} from 'recharts';
import { format, addDays, addMonths, addWeeks, parse, isBefore, isEqual, startOfISOWeek } from 'date-fns';
import { useEsmsStore } from '../../store/esms.store';
import type { EsmsTrendRow, EsmsGroupBy } from '../../types/esms';
import InfoTooltip from '../revenue/InfoTooltip';

function formatVND(v: number): string { return v.toLocaleString('en-US'); }

function labelForKey(key: string, groupBy: EsmsGroupBy): string {
  if (groupBy === 'month') {
    const [y, m] = key.split('-');
    return `Tháng ${parseInt(m, 10)}/${y}`;
  }
  if (groupBy === 'week') {
    const [y, w] = key.split('-W');
    return `Tuần ${parseInt(w, 10)}/${y}`;
  }
  return format(parse(key, 'yyyy-MM-dd', new Date()), 'dd/MM');
}

function fillGaps(data: EsmsTrendRow[], from: string, to: string, groupBy: EsmsGroupBy) {
  const map = new Map(data.map(d => [d.date, { ...d }]));
  const filled: EsmsTrendRow[] = [];
  if (groupBy === 'day') {
    let cur = parse(from, 'yyyy-MM-dd', new Date());
    const end = parse(to, 'yyyy-MM-dd', new Date());
    while (isBefore(cur, end) || isEqual(cur, end)) {
      const k = format(cur, 'yyyy-MM-dd');
      filled.push(map.get(k) || { date: k, sms_count: 0, total_cost: 0, success_count: 0, failed_count: 0 });
      cur = addDays(cur, 1);
    }
  } else if (groupBy === 'week') {
    let cur = startOfISOWeek(parse(from, 'yyyy-MM-dd', new Date()));
    const end = startOfISOWeek(parse(to, 'yyyy-MM-dd', new Date()));
    while (isBefore(cur, end) || isEqual(cur, end)) {
      const k = format(cur, "RRRR-'W'II");
      filled.push(map.get(k) || { date: k, sms_count: 0, total_cost: 0, success_count: 0, failed_count: 0 });
      cur = addWeeks(cur, 1);
    }
  } else {
    const fromMonth = from.substring(0, 7);
    const endMonth = to.substring(0, 7);
    let cur = parse(fromMonth + '-01', 'yyyy-MM-dd', new Date());
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const k = format(cur, 'yyyy-MM');
      filled.push(map.get(k) || { date: k, sms_count: 0, total_cost: 0, success_count: 0, failed_count: 0 });
      if (k >= endMonth) break;
      cur = addMonths(cur, 1);
    }
  }
  return filled;
}

const STACK_COLORS = ['#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#14b8a6'];

type ViewMode = 'total' | 'byType';

export default function EsmsTrendChart({ trend, isDark }: { trend: EsmsTrendRow[]; isDark: boolean }) {
  const trendGroupBy = useEsmsStore((s) => s.trendGroupBy);
  const fetchTrend = useEsmsStore((s) => s.fetchTrend);
  const fetchTrendByType = useEsmsStore((s) => s.fetchTrendByType);
  const trendByType = useEsmsStore((s) => s.trendByType);
  const trendByTypeLoading = useEsmsStore((s) => s.trendByTypeLoading);
  const trendByTypeError = useEsmsStore((s) => s.trendByTypeError);
  const reportFrom = useEsmsStore((s) => s.reportFrom);
  const reportTo = useEsmsStore((s) => s.reportTo);

  const [view, setView] = useState<ViewMode>('total');

  useEffect(() => {
    if (view === 'byType' && reportFrom && reportTo) fetchTrendByType(trendGroupBy);
  }, [view, trendGroupBy, reportFrom, reportTo, fetchTrendByType]);

  const filled = useMemo(() => (trend.length ? fillGaps(trend, reportFrom, reportTo, trendGroupBy) : []), [trend, reportFrom, reportTo, trendGroupBy]);

  const handleGroupBy = (g: EsmsGroupBy) => {
    if (g === trendGroupBy) return;
    fetchTrend(g);
    if (view === 'byType') fetchTrendByType(g);
  };

  const toggleClass = (active: boolean) => `px-2.5 py-1 text-xs font-medium rounded transition-colors ${active ? 'bg-cyan-500 text-white' : isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`;

  const tooltipText = 'Xu hướng eSMS theo thời gian.\n\nChế độ Tổng:\n• Cột xanh dương: Chi phí SMS mỗi kỳ (VND)\n• Đường xanh lá: Số SMS mỗi kỳ\n\nChế độ Theo loại:\n• Stacked bar: Số SMS chia theo SmsType.\n\nGroup: Daily / Weekly / Monthly.';

  const stackTypes = useMemo(() => {
    if (!trendByType) return [] as Array<{ key: string; smsType: number; label: string; color: string }>;
    const labels = trendByType.labels || {};
    const types = Object.keys(labels).map((k) => parseInt(k, 10)).sort((a, b) => a - b);
    return types.map((t, i) => ({ key: `count_${t}`, smsType: t, label: labels[t] || `Type ${t}`, color: STACK_COLORS[i % STACK_COLORS.length] }));
  }, [trendByType]);

  return (
    <div className={`backdrop-blur rounded-lg p-4 sm:p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Xu hướng eSMS
          <InfoTooltip isDark={isDark} text={tooltipText} />
        </h3>
        <div className="flex-1" />
        <div className={`inline-flex rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <button onClick={() => setView('total')} className={toggleClass(view === 'total')}>Tổng</button>
          <button onClick={() => setView('byType')} className={toggleClass(view === 'byType')}>Theo loại</button>
        </div>
        <div className={`inline-flex rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          <button onClick={() => handleGroupBy('day')} className={toggleClass(trendGroupBy === 'day')}>Daily</button>
          <button onClick={() => handleGroupBy('week')} className={toggleClass(trendGroupBy === 'week')}>Weekly</button>
          <button onClick={() => handleGroupBy('month')} className={toggleClass(trendGroupBy === 'month')}>Monthly</button>
        </div>
      </div>

      {view === 'total' ? (
        filled.length === 0 ? (
          <div className={`flex items-center justify-center h-40 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chưa có dữ liệu</div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={filled} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
              <XAxis
                dataKey="date"
                tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
                tickFormatter={(v: string) => labelForKey(v, trendGroupBy)}
                interval={trendGroupBy === 'day' && filled.length > 14 ? Math.ceil(filled.length / 14) : 0}
              />
              <YAxis yAxisId="left" tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }} tickFormatter={formatVND} label={{ value: 'Chi phí (VND)', angle: -90, position: 'insideLeft', fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11, offset: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }} label={{ value: 'Số SMS', angle: 90, position: 'insideRight', fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11, offset: 10 }} />
              <Tooltip
                contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`, borderRadius: '8px', color: isDark ? '#f8fafc' : '#111827' }}
                formatter={(value: any, name: any) => [name === 'total_cost' ? `${formatVND(value as number)} VND` : value, name === 'total_cost' ? 'Chi phí' : 'Số SMS']}
                labelFormatter={(v: string) => labelForKey(v, trendGroupBy)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v: string) => v === 'total_cost' ? 'Chi phí' : 'Số SMS'} />
              <Bar yAxisId="left" dataKey="total_cost" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={trendGroupBy === 'month' ? 40 : trendGroupBy === 'week' ? 28 : 20} />
              <Line yAxisId="right" type="monotone" dataKey="sms_count" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )
      ) : trendByTypeLoading ? (
        <div className={`flex items-center justify-center h-40 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Đang tải...</div>
      ) : trendByTypeError ? (
        <div className={`text-sm ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>{trendByTypeError}</div>
      ) : !trendByType || trendByType.data.length === 0 ? (
        <div className={`flex items-center justify-center h-40 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chưa có dữ liệu</div>
      ) : (
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={trendByType.data} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
            <XAxis
              dataKey="date"
              tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
              tickFormatter={(v: string) => labelForKey(v, trendGroupBy)}
              interval={trendGroupBy === 'day' && trendByType.data.length > 14 ? Math.ceil(trendByType.data.length / 14) : 0}
            />
            <YAxis tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }} tickFormatter={formatVND} label={{ value: 'Số SMS', angle: -90, position: 'insideLeft', fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11, offset: 10 }} />
            <Tooltip
              contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`, borderRadius: '8px', color: isDark ? '#f8fafc' : '#111827' }}
              formatter={(value: any, _name: any, item: any) => {
                const t = stackTypes.find((s) => s.key === item.dataKey);
                return [formatVND(value as number), t ? t.label : item.dataKey];
              }}
              labelFormatter={(v: string) => labelForKey(v, trendGroupBy)}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v: string) => {
              const t = stackTypes.find((s) => s.key === v);
              return t ? t.label : v;
            }} />
            {stackTypes.map((t) => (
              <Bar key={t.key} dataKey={t.key} stackId="sms" fill={t.color} barSize={trendGroupBy === 'month' ? 40 : trendGroupBy === 'week' ? 28 : 20} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
